import { useState } from "react";
import { CheckSquare, FileText, Mail, Phone, Video } from "lucide-react";
import { useLocaleState, useTranslate } from "ra-core";
import { Link } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { TimelineItem } from "../../domain/timeline";
import { emailDeliveryBadge } from "../../domain/email"; // GAVION outlook-parity
import { formatLeadDateTime } from "../../domain/torontoTime";
import { CallOutcomeBadge } from "../badges";
import { MessageBodyDialog } from "../../integrations/mail/MessageBodyDialog"; // GAVION batch 3: emails
import { MeetingNoteTimelineBody } from "../../meetings/MeetingNoteTimelineBody";
import { CallMeetingNotes, callNoteIds } from "../../meetings/CallMeetingNotes"; // GAVION granola-calls
import { CALL_OUTCOMES, type CallOutcome } from "../types";
// GAVION: B2-I2 outcome edit + recording player on call items
import { CallTimelineActions } from "../../dialer/CallTimelineActions";
import { TimelineBriefBlock } from "../../ai/brief/TimelineBriefBlock"; // GAVION call-brief-pipeline

const ICONS = {
  calls: Phone,
  notes: FileText,
  emails: Mail,
  meetings: Video,
  tasks: CheckSquare,
} as const;
const isOutcome = (v: unknown): v is CallOutcome =>
  typeof v === "string" && (CALL_OUTCOMES as readonly string[]).includes(v);

// GAVION outlook-parity: delivery badge of an outbound email (SES events or Outlook NDRs / inferred).
const deliveryOf = (item: TimelineItem) =>
  item.category === "emails"
    ? emailDeliveryBadge({
        id: item.source_id,
        provider: String(item.meta.provider ?? ""),
        direction: String(item.meta.direction ?? ""),
        status: String(item.meta.status ?? ""),
        // M2: no « Livré (probable) » on the rep's own synced Outlook mail.
        origin: typeof item.meta.origin === "string" ? item.meta.origin : null,
        // v_timeline.at is coalesce(sent_at, received_at, created_at): sent_at for a message that left.
        sent_at: item.at,
      })
    : null;

// GAVION batch 3: emails
const EmailOpenButton = ({ emailMessageId }: { emailMessageId: number }) => {
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-auto self-start px-1 py-0.5"
        onClick={() => setOpen(true)}
      >
        {translate("gavion.compose.message.open")}
      </Button>
      {open ? (
        <MessageBodyDialog
          open
          onOpenChange={setOpen}
          emailMessageId={emailMessageId}
        />
      ) : null}
    </>
  );
};

// GAVION open-pixel: « Ouvert N fois — dernière : … » (+ badge when every open looks automatic)
const EmailOpenSummary = ({
  meta,
  locale,
}: {
  meta: Record<string, unknown>;
  locale: "fr" | "en";
}) => {
  const translate = useTranslate();
  const count = Number(meta.open_count ?? 0);
  if (!Number.isFinite(count) || count <= 0) return null;
  const last =
    typeof meta.last_opened_at === "string" ? meta.last_opened_at : null;
  return (
    <p className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
      <span>
        {last
          ? translate("gavion.compose.message.opened_last", {
              smart_count: count,
              at: formatLeadDateTime(last, locale),
            })
          : translate("gavion.compose.message.opened", { smart_count: count })}
      </span>
      {meta.likely_auto_open === true ? (
        <Badge variant="outline">
          {translate("gavion.compose.message.auto_open")}
        </Badge>
      ) : null}
    </p>
  );
};

export const TimelineEntry = ({
  item,
  salesName,
}: {
  item: TimelineItem;
  salesName: string | null;
}) => {
  const translate = useTranslate();
  const [locale = "fr"] = useLocaleState();
  const Icon = ICONS[item.category] ?? FileText;
  // GAVION batch 3: emails (direction label)
  const kindLabel = item.kind.startsWith("email_")
    ? translate(
        item.kind === "email_in"
          ? "gavion.compose.message.inbound"
          : "gavion.compose.message.outbound",
      )
    : translate(`gavion.timeline.kind.${item.kind}`, {
        _: item.title ?? item.kind,
      });
  const seconds = Number(item.meta.billsec_s ?? item.meta.duration_s ?? 0);
  const delivery = deliveryOf(item);
  return (
    <li className="flex gap-3">
      <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="font-semibold">{kindLabel}</span>
          {item.kind === "call" && isOutcome(item.meta.outcome) ? (
            <CallOutcomeBadge outcome={item.meta.outcome} />
          ) : null}
          {/* GAVION contact-call: the call went to a contact, not the lead's main number */}
          {item.kind === "call" &&
          typeof item.meta.contact_name === "string" &&
          item.meta.contact_id != null ? (
            <Link
              className="underline"
              to={`/contacts/${Number(item.meta.contact_id)}/show`}
            >
              {/* GAVION demo-contact: a demo booked on the main line names its attendee, not « À … » */}
              {translate(
                item.meta.demo_linked === true
                  ? "gavion.dialer.contact_call.timeline_attendee"
                  : "gavion.dialer.contact_call.timeline_to",
                { contact: item.meta.contact_name },
              )}
            </Link>
          ) : null}
          {/* GAVION off-CRM call: logged after a call made from a cell */}
          {item.kind === "call" && item.meta.off_crm === true ? (
            <span className="text-muted-foreground">
              {translate("gavion.dialer.off_crm.timeline")}
            </span>
          ) : null}
          {item.kind === "call" && seconds > 0 ? (
            <span className="text-muted-foreground">
              {translate("gavion.timeline.duration", {
                minutes: Math.floor(seconds / 60),
                seconds: seconds % 60,
              })}
            </span>
          ) : null}
          {item.category === "emails" && item.meta.is_reply ? (
            <Badge variant="outline">
              {translate("gavion.compose.message.reply")}
            </Badge>
          ) : null}
          {delivery ? (
            <Badge
              variant={
                delivery === "bounced" || delivery === "complained"
                  ? "destructive"
                  : "outline"
              }
            >
              {translate(`gavion.email.delivery.badges.${delivery}`)}
            </Badge>
          ) : null}
          {item.category === "emails" &&
          item.meta.unsubscribe_request === true ? (
            <Badge variant="destructive">
              {translate("gavion.email.delivery.badges.unsubscribed")}
            </Badge>
          ) : null}
          {item.meta.source === "import" ? (
            <Badge variant="outline">
              {translate("gavion.timeline.imported")}
            </Badge>
          ) : null}
          <time dateTime={item.at} className="text-muted-foreground">
            {formatLeadDateTime(item.at, locale === "en" ? "en" : "fr")}
          </time>
          {salesName ? (
            <span className="text-muted-foreground">
              {translate("gavion.timeline.by", { name: salesName })}
            </span>
          ) : null}
        </div>
        {item.kind !== "call" && item.kind !== "company_note" && item.title ? (
          <p className="text-sm font-medium">{item.title}</p>
        ) : null}
        {/* GAVION batch 6: Granola notes render the full note (text-only blocks + suggested tasks) */}
        {item.kind === "meeting_note" ? (
          <MeetingNoteTimelineBody noteId={item.source_id} />
        ) : item.body_snippet ? (
          <p className="whitespace-pre-line text-sm">{item.body_snippet}</p>
        ) : null}
        {item.kind === "email_out" ? (
          <EmailOpenSummary
            meta={item.meta}
            locale={locale === "en" ? "en" : "fr"}
          />
        ) : null}
        {item.category === "emails" && item.meta.provider === "graph" ? (
          <EmailOpenButton emailMessageId={item.source_id} />
        ) : null}
        {/* GAVION granola-calls: the call's Granola notes, under the call */}
        {item.kind === "call" ? (
          <CallMeetingNotes noteIds={callNoteIds(item.meta.meeting_note_ids)} />
        ) : null}
        {/* GAVION call-brief-pipeline: the brief shown before the call, collapsed */}
        {item.kind === "call" ? (
          <TimelineBriefBlock brief={item.meta.brief} />
        ) : null}
        {/* GAVION: B2-I2 */}
        {item.kind === "call" ? <CallTimelineActions item={item} /> : null}
      </div>
    </li>
  );
};
